import { v4 as uuidv4 } from 'uuid';

/**
 * LDB QR Payment API Service
 * Handles QR payment generation and inquiry for Lao Development Bank
 */

// Base configuration
const LDB_CONFIG = {
  baseUrl: process.env.REACT_APP_LDB_BASE_URL || 'https://api.ldb.la',
  partnerId: process.env.REACT_APP_LDB_PARTNER_ID || '<Your API Key>',
  authToken: process.env.REACT_APP_LDB_AUTH_TOKEN || 'Bearer <access_token>'
};

/**
 * Generate headers for LDB API requests
 * @param {string} transactionId - Unique transaction ID (UUID v4)
 * @returns {Object} Headers object
 */
const generateHeaders = (transactionId = null) => {
  const txnId = transactionId || uuidv4();
  const now = new Date();
  const datetime = now.toISOString().replace('Z', '+0700'); // Format: 2020-12-17T17:26:40.979+0700
  
  return {
    'Authorization': LDB_CONFIG.authToken,
    'accept-language': 'EN',
    'content-type': 'application/json',
    'partnerId': LDB_CONFIG.partnerId,
    'x-client-transaction-datetime': datetime,
    'x-client-transaction-id': txnId
  };
};

/**
 * Query Transaction - Check payment status for a QR payment
 * Uses backend proxy to avoid CORS issues and handle authentication
 * @param {string} reqTxnId - x-client-transaction-id from the initiate transaction
 * @param {string} reference2 - Reference Number 2 from initiate transaction
 * @param {string} qrType - QR Type (default: 'LAO_QR')
 * @returns {Promise<Object>} Payment inquiry response
 */
export const queryTransaction = async (reqTxnId, reference2, qrType = 'LAO_QR') => {
  console.log('========== LDB QR PAYMENT INQUIRY (via Backend Proxy) ==========');
  console.log('Request Transaction ID:', reqTxnId);
  console.log('Reference2:', reference2);
  console.log('QR Type:', qrType);
  
  try {
    // Use backend proxy endpoint to query LDB API
    // This avoids CORS issues and centralizes LDB authentication
    const queryParams = new URLSearchParams({
      reference2: reference2
    });
    
    if (qrType) {
      queryParams.append('qrType', qrType);
    }
    
    const url = `${process.env.REACT_APP_API_URL}/ldb/query-transaction/${reqTxnId}?${queryParams}`;
    console.log('🔗 Request URL (Integrated API):', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    console.log('📊 Response Status:', response.status);
    
    const data = await response.json();
    console.log('📊 Response Data:', JSON.stringify(data, null, 2));
    console.log('===========================================');
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return {
      success: data.success,
      data: data.data,
      isPaid: data.isPaid,
      status: data.status,
      transactionId: data.transactionId,
      reference2: data.reference2
    };
    
  } catch (error) {
    console.error('❌ LDB QR Payment Inquiry Error:', error);
    console.error('Error details:', error.message);
    console.log('===========================================');
    
    return {
      success: false,
      error: error.message,
      status: error.status || 500
    };
  }
};

/**
 * Initiate QR Payment - Generate QR code for payment
 * @param {Object} paymentData - Payment information
 * @returns {Promise<Object>} QR generation response
 */
export const initiateQRPayment = async (paymentData) => {
  console.log('========== LDB QR PAYMENT INITIATE ==========');
  console.log('Payment Data:', JSON.stringify(paymentData, null, 2));
  
  try {
    const txnId = uuidv4();
    const headers = generateHeaders(txnId);
    
    const requestBody = {
      amount: paymentData.amount,
      reference1: paymentData.reference1 || paymentData.card_uid,
      reference2: paymentData.reference2 || `TKB${Date.now()}`,
      description: paymentData.description || 'Top up wallet',
      qrType: 'LAO_QR'
    };
    
    console.log('Request Body:', JSON.stringify(requestBody, null, 2));
    
    const url = `${LDB_CONFIG.baseUrl}/vboxConsumers/api/v1/qrpayment/initiate.service`;
    console.log('Request URL:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });
    
    console.log('Response Status:', response.status);
    
    const data = await response.json();
    console.log('Response Data:', JSON.stringify(data, null, 2));
    console.log('=============================================');
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return {
      success: true,
      data: data,
      reqTxnId: txnId,
      reference2: requestBody.reference2,
      status: response.status
    };
    
  } catch (error) {
    console.error('❌ LDB QR Payment Initiate Error:', error);
    console.error('Error details:', error.message);
    console.log('=============================================');
    
    return {
      success: false,
      error: error.message,
      status: error.status || 500
    };
  }
};

/**
 * Parse LDB payment status response
 * @param {Object} inquiryData - Response from queryTransaction
 * @returns {Object} Parsed payment status
 */
export const parsePaymentStatus = (inquiryData) => {
  if (!inquiryData || !inquiryData.data) {
    return {
      isPaid: false,
      status: 'unknown',
      message: 'No data received'
    };
  }
  
  const data = inquiryData.data;
  
  // Check payment status based on LDB response structure
  // Adjust these fields based on actual LDB API response
  const status = data.status || data.transactionStatus || 'unknown';
  const isPaid = status.toLowerCase() === 'success' || 
                 status.toLowerCase() === 'paid' || 
                 status.toLowerCase() === 'completed';
  
  return {
    isPaid: isPaid,
    status: status,
    message: data.message || data.description || '',
    transactionId: data.transactionId || data.txnId || '',
    amount: data.amount || 0,
    timestamp: data.timestamp || data.transactionDate || new Date().toISOString()
  };
};

export default {
  queryTransaction,
  initiateQRPayment,
  parsePaymentStatus
};
